import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button, Form, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

const AnamneseTabletPage = () => {
    const { codigo } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFinished, setIsFinished] = useState(false);
    const [error, setError] = useState(null);
    const [pacienteData, setPacienteData] = useState(null);
    const [respostas, setRespostas] = useState({}); // {pergunta_id: "texto da resposta"}

    useEffect(() => {
        const validarCodigo = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`/api/public/anamnese/${codigo}`);
                setPacienteData(response.data);
                setIsLoading(false);
            } catch (err) {
                setError("Código inválido ou expirado. Por favor, solicite um novo código na recepção.");
                setIsLoading(false);
            }
        };
        validarCodigo();
    }, [codigo]);

    const handleInputChange = (perguntaId, value) => {
        setRespostas(prev => ({ ...prev, [perguntaId]: value }));
    };

    // Função para renderizar o campo de entrada dinamicamente baseado no tipo
    const renderPerguntaInput = (p) => {
        switch (p.tipo_pergunta) {
            case 'booleano': // Tipo Sim/Não
                return (
                    <div className="mt-2">
                        <Form.Check
                            inline
                            label="Sim"
                            type="radio"
                            name={`pergunta-${p.id}`}
                            id={`pergunta-${p.id}-sim`}
                            checked={respostas[p.id] === 'Sim'}
                            onChange={() => handleInputChange(p.id, 'Sim')}
                            required
                        />
                        <Form.Check
                            inline
                            label="Não"
                            type="radio"
                            name={`pergunta-${p.id}`}
                            id={`pergunta-${p.id}-nao`}
                            checked={respostas[p.id] === 'Não'}
                            onChange={() => handleInputChange(p.id, 'Não')}
                            required
                        />
                    </div>
                );
            case 'multipla_escolha': // Tipo Select (Combo)
                const opcoes = p.opcoes ? (typeof p.opcoes === 'string' ? JSON.parse(p.opcoes) : p.opcoes) : [];
                return (
                    <Form.Select
                        value={respostas[p.id] || ''}
                        onChange={(e) => handleInputChange(p.id, e.target.value)}
                        required
                    >
                        <option value="">Selecione uma opção...</option>
                        {opcoes.map((opcao, index) => (
                            <option key={index} value={opcao}>{opcao}</option>
                        ))}
                    </Form.Select>
                );
            case 'texto_livre':
            default:
                return (
                    <Form.Control as="textarea" rows={2} placeholder="Sua resposta..." value={respostas[p.id] || ''} onChange={(e) => handleInputChange(p.id, e.target.value)} required />
                );
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = {
                tipo_ficha: pacienteData.tipo_ficha,
                respostas: Object.keys(respostas).map(id => ({
                    pergunta_id: id,
                    resposta: respostas[id]
                }))
            };
            await axios.post(`/api/public/anamnese/${codigo}/submit`, payload);
            setIsFinished(true);
        } catch (err) {
            alert("Erro ao enviar anamnese. Verifique sua conexão.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <Container className="d-flex justify-content-center align-items-center vh-100">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <h4>Carregando sua ficha...</h4>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    if (isFinished) {
        return (
            <Container className="d-flex justify-content-center align-items-center vh-100">
                <Card className="text-center shadow-lg p-5 border-0" style={{ maxWidth: '500px' }}>
                    <FontAwesomeIcon icon={faCheckCircle} size="5x" className="text-success mb-4" />
                    <h3>Obrigado, {pacienteData?.paciente_nome}!</h3>
                    <p className="lead text-muted">Sua ficha de anamnese foi enviada com sucesso.</p>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <Card className="shadow-sm border-0">
                <Card.Header className="bg-primary text-white py-3">
                    <h4 className="mb-0">Ficha de Anamnese</h4>
                    <small>Paciente: {pacienteData?.paciente_nome} | Especialidade: {pacienteData?.especialidade}</small>
                </Card.Header>
                <Card.Body>
                    <Alert variant="info">
                        Suas respostas ajudam nossa equipe médica a oferecer o melhor tratamento possível.
                    </Alert>
                    
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            {pacienteData?.perguntas?.map((p) => (
                                <Col md={12} key={p.id} className="mb-4">
                                    <Form.Group>
                                        <Form.Label className="fw-bold">{p.pergunta}</Form.Label>
                                        {renderPerguntaInput(p)}
                                    </Form.Group>
                                </Col>
                            ))}
                        </Row>
                        
                        <div className="d-grid mt-4">
                            <Button variant="success" size="lg" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faPaperPlane} className="me-2" /> Enviar Respostas</>}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default AnamneseTabletPage;
